const toStringFailsafe = (value: unknown) => {
  if (value === null || value === undefined) {
    return value;
  }

  return String(value).toUpperCase();
};

export const sort = <T>(orig: T[], propName: keyof T, caseSensitive?: boolean) => {
  if (!orig) {
    return orig;
  }

  return orig.sort((a, b) => {
    const valueA = caseSensitive ? toStringFailsafe(a[propName]) : a[propName];
    const valueB = caseSensitive ? toStringFailsafe(b[propName]) : b[propName];

    if (valueA < valueB) {
      return -1;
    }
    if (valueA > valueB) {
      return 1;
    }

    return 0;
  });
};

/**
 * Character category for sorting: digits first (0), then letters (1), then other (2).
 */
const charCategory = (ch: string): number => {
  if (ch >= '0' && ch <= '9') return 0;
  if ((ch >= 'a' && ch <= 'z') || (ch >= 'A' && ch <= 'Z')) return 1;
  return 2;
};

/**
 * Comparator for consistent sorting: case-insensitive, digits first (0-9), then A-Z, then Unicode.
 * Use as: `array.sort(compareStrings)` for string arrays,
 * or `array.sort((a, b) => compareStrings(a.name, b.name))` for objects.
 */
export const compareStrings = (a: string, b: string): number => {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;

  const aLower = a.toLowerCase();
  const bLower = b.toLowerCase();
  const minLen = Math.min(aLower.length, bLower.length);

  for (let i = 0; i < minLen; i++) {
    const catA = charCategory(aLower[i]);
    const catB = charCategory(bLower[i]);

    if (catA !== catB) return catA - catB;
    if (aLower[i] < bLower[i]) return -1;
    if (aLower[i] > bLower[i]) return 1;
  }

  return aLower.length - bLower.length;
};

/**
 * Comparator for hierarchical symbolGroup sorting.
 * Splits by `/`, compares segment by segment using `compareStrings`.
 */
export const compareSymbolGroups = (a: string, b: string): number => {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;

  const segA = a.split('/');
  const segB = b.split('/');
  const minLen = Math.min(segA.length, segB.length);

  for (let i = 0; i < minLen; i++) {
    const cmp = compareStrings(segA[i], segB[i]);
    if (cmp !== 0) return cmp;
  }

  return segA.length - segB.length;
};
